import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ShoppingCart, Package } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function KioskPreview() {
  const [sp] = useSearchParams();
  const [kiosks, setKiosks] = useState([]);
  const [kioskId, setKioskId] = useState(sp.get("kiosk") || "");
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/kiosks").then((r) => { setKiosks(r.data); if (!kioskId && r.data.length) setKioskId(r.data[0].id); }); }, []); // eslint-disable-line
  const code = kiosks.find((k) => k.id === kioskId)?.code;
  useEffect(() => {
    if (!code) return;
    setLoading(true);
    api.get(`/kiosk/${code}/products`).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
    setCategory("All");
  }, [code]);

  const categories = ["All", ...Array.from(new Set(items.map((p) => p.category)))];
  const visible = category === "All" ? items : items.filter((p) => p.category === category);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><ShoppingCart className="h-6 w-6 text-[#7C3AED]" /> Customer preview</h1>
          <p className="text-slate-500 mt-1">Exactly what shoppers see on this kiosk — in planogram order. Reflects the last saved planogram &amp; live stock.</p>
        </div>
        <Select value={kioskId} onValueChange={setKioskId}>
          <SelectTrigger data-testid="preview-kiosk-select" className="w-64"><SelectValue placeholder="Select kiosk" /></SelectTrigger>
          <SelectContent>{kiosks.map((k) => <SelectItem key={k.id} value={k.id}>{k.name} ({k.code})</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="rounded-3xl border border-stone-200 dark:border-slate-800 bg-stone-50 dark:bg-slate-900 overflow-hidden max-w-5xl">
        <div className="flex items-center gap-3 px-5 py-4 bg-white dark:bg-slate-950 border-b border-stone-200 dark:border-slate-800 overflow-x-auto no-scrollbar">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`preview-category-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`px-6 min-h-[52px] rounded-2xl text-lg font-semibold whitespace-nowrap transition-all ${category === c ? "bg-[var(--brand-primary,#7C3AED)] text-white shadow-md" : "bg-white dark:bg-slate-900 text-stone-600 dark:text-slate-300 border border-stone-200 dark:border-slate-700"}`}
            >{c || "Uncategorised"}</button>
          ))}
        </div>
        <div className="p-5">
          {loading && <p className="text-center text-stone-400 py-16">Loading…</p>}
          {!loading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="preview-grid">
              {visible.map((p, i) => (
                <div key={p.id} data-testid={`preview-item-${i}`} className="bg-white dark:bg-slate-950 rounded-3xl border border-stone-200 dark:border-slate-700 overflow-hidden flex flex-col">
                  <div className="aspect-square bg-stone-50 dark:bg-slate-800">
                    {p.image_url ? <img src={resolveUrl(p.image_url)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center text-stone-300"><Package className="h-8 w-8" /></div>}
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 leading-tight">{p.name}</p>
                    <p className="text-base font-bold text-[#7C3AED] mt-auto pt-2">£{Number(p.price_incl_vat || 0).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && !visible.length && <p className="text-center text-stone-400 py-16">No products available on this kiosk (nothing in stock, or no planogram yet).</p>}
        </div>
      </div>
    </div>
  );
}
